/**
 * 设置页宠物那一栏「允许 TA 谈恋爱」（WIRE-01 A4，总账 N-05；文案 I 10-10 定稿）：GET／PUT /pets/{pet_id}/social-prefs。
 * 依据用户「恋爱八条」，但界面上只说主人需要知道的：不列规则（不写恋人的条件），也不提主人去私聊里插手——宠物的社交它自己做主（I 10-10）。
 * - 原创居民（origin＝adopted_original，居民不能谈）、或者后端说这只的设置读不出来（readable＝false）：任一命中，整项不显示，不放置灰开关；
 *   居民连读也不读。这一项还没开放（演示模式、后端没挂上）也不显示。
 * - 开关下面一句说明；还没设过（defaulted）再加一句小字「还没设过，现在是默认的「开／关」。」——开还是关照后端回包的 allow_romance 写，前端不写死。
 * - 切换即保存：带读到的 version 作 expected_version，带幂等键。
 *   - 409 先认 details.original：那次其实存好了，用它刷新；
 *   - 版本对不上（VERSION_CONFLICT）：重读一次，开关回到最新值，下面一句「刚刚有了别的修改，已经换成最新的设置。」（常驻播报区礼貌地念）；
 *   - 回执丢了（断网、超时、5xx）或者那次还在处理：开关回到原来的样子，下面一句「这次没存上，稍后再试。」；再点同一下，原样重发第一次的请求体和键
 *     （同 CREATE-PET-RED-01 的原则：后端按幂等键认，存好了的会回原结果，不会多存一份）；改成另一个值才算新的一次，换新键、用此刻读到的版本。
 * - 只有家庭管理员能改（后端 PUT 要 manage）：照护的家人看得到开关、不能点，下面一句「这一项由家庭管理员来改。」；万一后端回 403 也这样说。
 */
import { useLayoutEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { HouseholdPetBrief, PetSocialPrefsUpdate, PetSocialPrefsView } from "@/shared/contracts";
import { isApiError, toApiError } from "@/shared/api/errors";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { env } from "@/shared/config/env";
import { useServices } from "@/shared/services/registry";
import { useCurrentHousehold } from "@/shared/session/householdContext";
import { Button } from "@/shared/ui";

/** 按账号和宠物分：切宠物、换账号不串。 */
export const socialPrefsKey = (userId: string, petId: string) => ["pets", "social-prefs", userId, petId] as const;

export const ROMANCE_TITLE = "允许 TA 谈恋爱";
export const ROMANCE_NOTE = "打开时，TA 遇到投缘的伙伴，可以自己决定要不要在一起。关掉只会挡住以后的新恋情，已经在一起的不受影响。";
export const ROMANCE_CONFLICT = "刚刚有了别的修改，已经换成最新的设置。";
export const ROMANCE_FAILED = "这次没存上，稍后再试。";
export const ROMANCE_ADMIN = "这一项由家庭管理员来改。";

/** 还没设过时那句小字：开还是关照后端回包写。 */
export function romanceDefaultText(view: Pick<PetSocialPrefsView, "allow_romance">): string {
  return `还没设过，现在是默认的「${view.allow_romance ? "开" : "关"}」。`;
}

/** 这一项显不显示：原创居民、读不出来的都不显示（不放置灰开关）。 */
export function romanceShown(origin: HouseholdPetBrief["origin"] | null | undefined, view: Pick<PetSocialPrefsView, "readable"> | null): boolean {
  if (origin === "adopted_original") return false;
  return view ? view.readable !== false : true;
}

function isPrefsView(value: unknown, petId: string): value is PetSocialPrefsView {
  if (!value || typeof value !== "object") return false;
  const view = value as Partial<PetSocialPrefsView>;
  return view.pet_id === petId && typeof view.allow_romance === "boolean" && typeof view.version === "number";
}

/** 保存没成的时候怎么办（顺序：先认 original，再看版本冲突、还在处理、权限、回执丢了）。 */
export type RomanceSettle =
  | { kind: "saved"; view: PetSocialPrefsView }
  | { kind: "reread" }
  | { kind: "resend" }
  | { kind: "admin" }
  | { kind: "failed" };

export function settleRomanceError(error: unknown, petId: string): RomanceSettle {
  const err = toApiError(error);
  const original = err.status === 409 ? err.details?.original : undefined;
  if (isPrefsView(original, petId)) return { kind: "saved", view: original };
  if (err.code === "VERSION_CONFLICT") return { kind: "reread" };
  // 同一个键配了别的请求（前端不会这样发）：那次已经完成就重读换成最新的；还在处理就和回执丢了一样，留着这一次再原样重发
  if (err.code === "IDEMPOTENCY_KEY_REUSED") return err.details?.original_state === "in_progress" ? { kind: "resend" } : { kind: "reread" };
  if (err.code === "IDEMPOTENCY_IN_PROGRESS") return { kind: "resend" };
  if (err.status === 403) return { kind: "admin" };
  if (err.kind === "network" || err.kind === "timeout" || (err.status ?? 0) >= 500) return { kind: "resend" };
  return { kind: "failed" };
}

type Sent = { petId: string; body: PetSocialPrefsUpdate; key: string };

export function RomanceSetting({ pet }: { pet: HouseholdPetBrief | null }) {
  if (!pet || !romanceShown(pet.origin, null)) return null;
  return <RomanceOwner petId={pet.pet_id} />;
}

type PrefsScope = { signature: string; id: number; key: readonly unknown[]; owns: () => boolean };
const prefsServices = new WeakMap<object, number>();
let prefsSequence = 0;

/** Cache ownership stays inside this consumer. The exported user/pet prefix is
 * unchanged; service object identity and mount/permission lifetime are local,
 * never API credentials or invented server principal fields. */
function RomanceOwner({ petId }: { petId: string }) {
  const { pets } = useServices();
  const { userId, household } = useCurrentHousehold();
  let service = prefsServices.get(pets);
  if (!service) { service = ++prefsSequence; prefsServices.set(pets, service); }
  const signature = JSON.stringify([env.dataMode, userId, petId, service, household?.household_id, household?.role]);
  const latest = useRef<PrefsScope | null>(null);
  const makeScope = (): PrefsScope => {
    const id = ++prefsSequence;
    const value: PrefsScope = { signature, id, key: [...socialPrefsKey(userId ?? "-", petId), env.dataMode, "service", service, "owner", id], owns: () => latest.current === value };
    return value;
  };
  const [scope, setScope] = useState(makeScope);
  if (scope.signature !== signature) {
    const next = makeScope(); latest.current = next; setScope(next);
    return null;
  }
  latest.current = scope;
  if (env.dataMode === "live" && !userId?.trim()) return null;
  return <RomanceSwitch key={scope.id} petId={petId} scope={scope} />;
}

function RomanceSwitch({ petId, scope }: { petId: string; scope: PrefsScope }) {
  const life = useRef({ mounted: true, generation: 0 });
  useLayoutEffect(() => {
    const frame = life.current; frame.mounted = true;
    return () => { frame.mounted = false; frame.generation++; };
  }, []);
  const current = () => scope.owns() && life.current.mounted;
  const attempt = () => {
    const generation = life.current.generation;
    return () => current() && generation === life.current.generation;
  };
  const services = useServices();
  const queryClient = useQueryClient();
  const { userId, household } = useCurrentHousehold();
  const key = scope.key;
  const prefs = useQuery({
    queryKey: key,
    queryFn: async ({ signal }) => {
      const valid = attempt();
      if (!valid()) throw new Error("Social preferences context expired");
      try {
        const result = await services.pets.socialPrefs(petId, signal);
        if (!valid()) throw new Error("Social preferences context expired");
        return result;
      } catch (error) {
        if (!valid()) throw new Error("Social preferences context expired");
        throw error;
      }
    },
    enabled: env.dataMode !== "live" || Boolean(userId?.trim()),
  });
  /** 发出去还没收到回音的那一次：再点同一下原样重发（同键同体）。 */
  const sent = useRef<Sent | null>(null);
  const [saving, setSaving] = useState<boolean | null>(null);
  const [conflict, setConflict] = useState(false);
  const [problem, setProblem] = useState<"failed" | "admin" | null>(null);

  if (prefs.isPending) return null;
  if (prefs.isError) {
    if (isApiError(prefs.error) && prefs.error.isCapabilityUnavailable) return null;
    return (
      <div className="ps-settings-romance">
        <strong>{ROMANCE_TITLE}</strong>
        <p className="ps-muted ps-settings-romance__line" role="status">
          这次没读到，稍后再试。
        </p>
        <Button variant="ghost" loading={prefs.isFetching} onClick={() => { if (current()) void prefs.refetch(); }}>
          再试一次
        </Button>
      </div>
    );
  }
  const view = prefs.data;
  if (!romanceShown(null, view)) return null;
  const admin = household?.role === "admin";
  const checked = saving ?? view.allow_romance;

  const toggle = async (next: boolean) => {
    if (!current() || saving !== null || !admin) return;
    const valid = attempt();
    setConflict(false);
    setProblem(null);
    const last = sent.current;
    const req: Sent = last && last.petId === petId && last.body.allow_romance === next
      ? last
      : { petId, body: { allow_romance: next, expected_version: view.version }, key: newIdempotencyKey("social-prefs") };
    sent.current = req;
    setSaving(next);
    try {
      const saved = await services.pets.saveSocialPrefs(req.petId, req.body, req.key);
      if (!valid()) return;
      sent.current = null;
      queryClient.setQueryData(key, saved);
    } catch (error) {
      if (!valid()) return;
      const settle = settleRomanceError(error, petId);
      if (settle.kind === "saved") {
        sent.current = null;
        queryClient.setQueryData(key, settle.view);
      } else if (settle.kind === "reread") {
        sent.current = null;
        if (!valid()) return;
        const fresh = await prefs.refetch();
        if (!valid()) return;
        if (fresh.isError) setProblem("failed");
        else setConflict(true);
      } else if (settle.kind === "resend") {
        setProblem("failed");
      } else {
        sent.current = null;
        setProblem(settle.kind === "admin" ? "admin" : "failed");
      }
    } finally {
      if (valid()) setSaving(null);
    }
  };

  return (
    <div className="ps-settings-romance">
      <label className="ps-switch-row ps-settings-switch">
        <span>
          <strong>{ROMANCE_TITLE}</strong>
          <small>{ROMANCE_NOTE}</small>
          {view.defaulted ? <small>{romanceDefaultText(view)}</small> : null}
        </span>
        <input type="checkbox" role="switch" className="ps-switch" checked={checked} disabled={!admin || saving !== null} aria-busy={saving !== null || undefined} onChange={(e) => void toggle(e.target.checked)} />
      </label>
      {!admin ? <p className="ps-muted ps-settings-romance__line">{ROMANCE_ADMIN}</p> : null}
      <p className={conflict ? "ps-muted ps-settings-romance__line" : "visually-hidden"} aria-live="polite" aria-atomic="true" data-testid="romance-live">
        {conflict ? ROMANCE_CONFLICT : ""}
      </p>
      {problem ? (
        <p className="ps-form-error ps-settings-romance__line" role="alert">
          {problem === "admin" ? ROMANCE_ADMIN : ROMANCE_FAILED}
        </p>
      ) : null}
    </div>
  );
}
